import type { MapCameraChangedEvent } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_MAP_ZOOM, MARKER_MOUNT_DELAY_MS } from './constants';
import type { MapViewport } from './types';
import { getMarkerPlacementViewport, viewportFromMap } from './utils';

interface UseMapViewportOptions {
  defaultCenter: google.maps.LatLngLiteral;
  hasCoordinates: boolean;
  markerCenter: google.maps.LatLngLiteral;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
}

export function useMapViewport({
  defaultCenter,
  hasCoordinates,
  markerCenter,
  mapRef,
}: UseMapViewportOptions) {
  const mapViewportRef = useRef<MapViewport>({
    center: defaultCenter,
    zoom: DEFAULT_MAP_ZOOM,
  });
  const hadCoordinatesRef = useRef(false);
  const [mapViewport, setMapViewport] = useState<MapViewport>(
    () => mapViewportRef.current,
  );
  const [showMapMarker, setShowMapMarker] = useState(false);

  const syncViewport = useCallback((nextViewport: MapViewport) => {
    mapViewportRef.current = nextViewport;
    setMapViewport(nextViewport);
  }, []);

  const handleMapReady = useCallback(
    (map: google.maps.Map) => {
      const nextViewport = viewportFromMap(map);
      if (nextViewport) {
        syncViewport(nextViewport);
      }
    },
    [syncViewport],
  );

  const captureMapViewport = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const nextViewport = viewportFromMap(map);
    if (nextViewport) {
      syncViewport(nextViewport);
    }
  }, [mapRef, syncViewport]);

  const handleCameraChanged = useCallback(
    (ev: MapCameraChangedEvent) => {
      const { center, zoom } = ev.detail;
      if (!center || zoom == null) {
        return;
      }

      syncViewport({ center, zoom });
    },
    [syncViewport],
  );

  useEffect(() => {
    if (!hasCoordinates) {
      setShowMapMarker(false);
      hadCoordinatesRef.current = false;
      return undefined;
    }

    const justPlaced = !hadCoordinatesRef.current;
    hadCoordinatesRef.current = true;

    const markerTimer = window.setTimeout(
      () => {
        setShowMapMarker(true);
      },
      justPlaced ? MARKER_MOUNT_DELAY_MS : 0,
    );

    syncViewport(
      getMarkerPlacementViewport(
        justPlaced,
        markerCenter,
        mapViewportRef.current,
      ),
    );

    return () => {
      window.clearTimeout(markerTimer);
    };
  }, [hasCoordinates, markerCenter.lat, markerCenter.lng, syncViewport]);

  return {
    mapCenter: mapViewport.center,
    mapZoom: mapViewport.zoom,
    showMapMarker,
    setShowMapMarker,
    handleMapReady,
    handleCameraChanged,
    captureMapViewport,
  };
}
